import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoPlay } from "react-icons/io5";
import { loginAsGuest } from "../services/authService";
import { useAuth } from "../services/AuthContext";
import { getStoredLanguage } from "../services/language";
import {
  GUEST_LINK_KINDS,
  getActiveGuestLinkPass,
  isGuestSessionUser,
  normalizeGuestLink,
  resolveSafeGuestLinkTarget,
  writeGuestLinkPass,
} from "../utils/guestLinkAccess";
import "./GuestLaunch.css";

const COPY = {
  es: {
    puzzle: "Cargando el puzzle...",
    room: "Entrando a la sala de espera...",
    asGuest: "Entrarás como invitado, sin registrarte.",
    errorTitle: "No se pudo continuar",
    errorDefault: "No se pudo abrir el enlace. Inténtalo nuevamente.",
    retry: "Reintentar",
    back: "Volver",
  },
  en: {
    puzzle: "Loading the puzzle...",
    room: "Entering the waiting room...",
    asGuest: "You will join as a guest, no sign-up needed.",
    errorTitle: "Could not continue",
    errorDefault: "The link could not be opened. Please try again.",
    retry: "Try again",
    back: "Back",
  },
  ru: {
    puzzle: "Загружаем головоломку...",
    room: "Входим в зал ожидания...",
    asGuest: "Вы войдёте как гость, без регистрации.",
    errorTitle: "Не удалось продолжить",
    errorDefault: "Не удалось открыть ссылку. Попробуйте ещё раз.",
    retry: "Повторить",
    back: "Назад",
  },
  de: {
    puzzle: "Puzzle wird geladen...",
    room: "Warteraum wird betreten...",
    asGuest: "Du spielst als Gast, ohne Registrierung.",
    errorTitle: "Fortfahren nicht möglich",
    errorDefault: "Der Link konnte nicht geöffnet werden. Bitte versuche es erneut.",
    retry: "Erneut versuchen",
    back: "Zurück",
  },
};

// Entry point for shared links (/p/ID, /t/ID or the long /game links). Visitors without
// an account get a temporary guest session limited to this link; no registration or
// terms are asked until they leave the puzzle or the match.
const GuestLinkEntry = () => {
  const { kind = "", id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { login: authLogin, user, loading: authLoading } = useAuth();
  const link = useMemo(() => normalizeGuestLink(kind, id), [kind, id]);
  const target = useMemo(() => {
    if (!link) return "";
    const params = new URLSearchParams(location.search || "");
    return resolveSafeGuestLinkTarget(link, params.get("to"));
  }, [link, location.search]);
  const [errorMessage, setErrorMessage] = useState("");
  const startedRef = useRef(false);
  const language = String(getStoredLanguage() || "es").slice(0, 2).toLowerCase();
  const copy = COPY[language] || COPY.en;

  const enter = useCallback(async () => {
    if (!link || startedRef.current) return;
    startedRef.current = true;
    setErrorMessage("");

    try {
      if (!user) {
        const response = await loginAsGuest();
        const guestToken = String(response?.data?.token || "").trim();
        if (!guestToken) throw new Error(copy.errorDefault);
        writeGuestLinkPass(jwtDecode(guestToken), link);
        authLogin(guestToken, { persist: true });
      } else if (isGuestSessionUser(user) && getActiveGuestLinkPass(user)) {
        // A link guest opening another shared link moves their pass to the new one.
        writeGuestLinkPass(user, link);
      }

      navigate(target, { replace: true });
    } catch (error) {
      console.error("Guest link entry failed:", error);
      startedRef.current = false;
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          copy.errorDefault
      );
    }
  }, [authLogin, copy, link, navigate, target, user]);

  useEffect(() => {
    if (authLoading) return;
    if (!link) {
      navigate("/", { replace: true });
      return;
    }
    enter();
  }, [authLoading, enter, link, navigate]);

  if (!link) return null;

  return (
    <main className="xima-guest-launch">
      <div className="xima-guest-launch-card">
        <img src="/mejor-logo-336.png" alt="XIMA" />
        <span className="xima-guest-launch-spinner" aria-hidden="true">
          <IoPlay />
        </span>
        <h1>{link.kind === GUEST_LINK_KINDS.puzzle ? copy.puzzle : copy.room}</h1>
        {!user ? <p>{copy.asGuest}</p> : null}

        {errorMessage ? (
          <div className="xima-guest-launch-error" role="alert">
            <strong>{copy.errorTitle}</strong>
            <span>{errorMessage}</span>
            <div>
              <button type="button" onClick={enter}>
                {copy.retry}
              </button>
              <button type="button" className="is-secondary" onClick={() => navigate("/")}>
                {copy.back}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
};

export default GuestLinkEntry;
